import { useEffect, useState } from 'react'
import { MapPin, Gauge, Navigation, MountainSnow, Cpu, SignalHigh, SignalZero, Settings2, LogOut, User, ServerCog, Fuel, Timer, MapPinOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Drawer from './Drawer'
import { useConfirm } from './ConfirmDialog'
import DeviceActionsModal from './DeviceActionsModal'
import DeviceCommands from './DeviceCommands'
import { useAuth } from '../contexts/AuthContext'
import AdvancedDeviceModal from './AdvancedDeviceModal'
import { useWS } from '../contexts/WSContext'
import { equipmentOf, statusOf, telemetryFresh, TELEMETRY_TIMEOUT_S } from '../equipment'
import { fmtInt, fmtNum, fmtRelative } from '../i18n/format'
import { statusText } from '../i18n/status'

const fmt = (v, d = 1) => (v != null ? Number(v).toFixed(d) : '—')
const compass = (t, deg) => {
  if (deg == null) return '—'
  const pts = t('common:compass', { returnObjects: true })
  const arr = Array.isArray(pts) ? pts : ['N', 'NE', 'L', 'SE', 'S', 'SO', 'O', 'NO']
  return arr[Math.round(deg / 45) % 8]
}

function Field({ icon: Icon, label, children }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500"><Icon size={12} /> {label}</div>
      <div className="text-sm text-slate-700 dark:text-slate-200 tabular-nums mt-0.5">{children}</div>
    </div>
  )
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-2">
      <dt className="text-xs text-slate-400 dark:text-slate-500">{k}</dt>
      <dd className="text-sm text-slate-700 dark:text-slate-200 truncate ml-3">{v}</dd>
    </div>
  )
}

export default function DeviceDetailDrawer({ device, open, onClose }) {
  const { t } = useTranslation()
  const { categories, mines } = useWS()
  const { can } = useAuth()
  const [actionsOpen, setActionsOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [loggingOff, setLoggingOff] = useState(false)
  const [logoffResult, setLogoffResult] = useState(null)
  const [confirmDialog, ask] = useConfirm()

  // Fechar o drawer ou trocar de equipamento fecha os modais: eles ficavam armados e
  // reapareciam com o nome de outra máquina.
  const deviceName = device?.name
  useEffect(() => {
    setActionsOpen(false); setAdvancedOpen(false); setLogoffResult(null)
  }, [deviceName])
  useEffect(() => {
    if (!open) { setActionsOpen(false); setAdvancedOpen(false) }
  }, [open])

  if (!device) return <Drawer open={open} onClose={onClose} />

  const st = statusOf(device)
  const eq = equipmentOf(device, categories, mines)
  const online = st.key !== 'down'
  const loggedIn = (device.operatorName ?? '') !== ''
  const lastSeen = fmtRelative(device.lastSeen)
  const sinceOrNever = (ts) => (ts ? fmtRelative(ts) : <span className="text-slate-400 dark:text-slate-500">{t('ops:drawer.never')}</span>)
  // Comunicando sem posição: o VMC 0.8 sem fix de GPS só manda o sinal de vida, e os
  // campos de posição abaixo continuam mostrando a última conhecida.
  const noGps = online && !telemetryFresh(device)

  // O comando derruba o operador da cabine (a HMI volta ao login), então pede
  // confirmação; e o resultado reporta se o IoT aceitou publicar (published), em vez de
  // sumir em silêncio quando não chegou ao VMC.
  const runForceLogoff = async () => {
    setLoggingOff(true)
    setLogoffResult(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(device.name)}/force-logoff`, { method: 'POST' })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setLogoffResult(data.published === false
        ? { ok: false, msg: t('ops:drawer.force_logoff_not_delivered') }
        : { ok: true, msg: t('ops:drawer.force_logoff_sent') })
    } catch (err) {
      setLogoffResult({ ok: false, msg: err.message })
    } finally {
      setLoggingOff(false)
    }
  }

  const forceLogoff = () => ask({
    title: t('ops:drawer.force_logoff'),
    message: t('ops:drawer.force_logoff_confirm', { name: eq.name || device.name }),
    confirmLabel: t('ops:drawer.force_logoff'),
    danger: true,
    onConfirm: runForceLogoff,
  })

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={eq.name || device.name}
      subtitle={eq.model ? `${eq.label} · ${eq.model}` : eq.label}
      icon={
        <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 shrink-0">
          <span className="equip-icon block w-6 h-6" style={{ WebkitMaskImage: `url(/equipments/${eq.svg}.svg)`, maskImage: `url(/equipments/${eq.svg}.svg)` }} />
        </div>
      }
      footer={
        // O modal só oferece apagar eventos e remover o equipamento, as duas de admin.
        can('admin') ? (
          <button onClick={() => setActionsOpen(true)} className="w-full inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
            <Settings2 size={14} /> {t('ops:drawer.equipment_actions')}
          </button>
        ) : null
      }
    >
      <div className="px-5 py-4 space-y-4">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${st.soft} ${st.text} text-xs font-medium`}>
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {statusText(t, st)}
          </span>
          <span title={device.vmcrpcVersion ? t('ops:drawer.protocol_version', { version: device.vmcrpcVersion }) : undefined}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-xs font-medium">
            <Cpu size={11} /> VMC
            {device.vmcrpcVersion && <span className="font-mono font-normal text-[10px] text-slate-400 dark:text-slate-500">{device.vmcrpcVersion}</span>}
          </span>
          {noGps && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-300 text-xs font-medium">
              <MapPinOff size={11} /> {t('ops:drawer.no_gps')}
            </span>
          )}
          <span className={online ? 'text-status-comms' : 'text-status-down'} title={t('ops:devices.comms')}>
            {online ? <SignalHigh size={14} /> : <SignalZero size={14} />}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Field icon={MapPin} label={t('ops:drawer.position')}>{fmt(device.lastLat, 5)}°, {fmt(device.lastLon, 5)}°</Field>
          <Field icon={MountainSnow} label={t('ops:drawer.altitude')}>{fmt(device.lastAltM)} m</Field>
          <Field icon={Gauge} label={t('ops:drawer.speed')}>{fmt(device.lastSpeedKmh)} km/h</Field>
          <Field icon={Navigation} label={t('ops:drawer.heading')}>{fmt(device.lastHeadingDeg)}° {compass(t, device.lastHeadingDeg)}</Field>
          <Field icon={Fuel} label={t('ops:drawer.fuel')}>{device.lastFuelPct != null ? `${fmtNum(device.lastFuelPct)}%` : fmt(null)}</Field>
          <Field icon={Timer} label={t('ops:drawer.horimeter')}>{device.lastHorimeterH != null ? `${fmtNum(device.lastHorimeterH, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h` : fmt(null)}</Field>
        </div>
        {noGps && (
          <p className="-mt-2 text-[11px] leading-snug text-violet-600 dark:text-violet-300">{t('ops:drawer.no_gps_hint', { secs: TELEMETRY_TIMEOUT_S })}</p>
        )}

        <dl className="divide-y divide-slate-100 dark:divide-slate-800 border-y border-slate-100 dark:border-slate-800">
          <Row k={t('common:entities.operator')} v={loggedIn
            ? <span className="inline-flex items-center gap-1.5"><User size={13} className="text-status-op" /> {device.operatorName}</span>
            : <span className="text-slate-400 dark:text-slate-500">{t('ops:drawer.no_operator')}</span>} />
          <Row k={t('ops:drawer.mine_pit')} v={eq.mine ? (eq.pit ? `${eq.mine} · ${eq.pit}` : eq.mine) : '—'} />
          <Row k={t('ops:drawer.device_iot')} v={<span className="font-mono text-xs">{device.name}</span>} />
          <Row k={t('ops:drawer.profile_iot')} v={device.profileName} />
          <Row k={t('ops:drawer.events_received')} v={fmtInt(device.eventCount ?? 0)} />
          <Row k={t('ops:drawer.last_seen')} v={lastSeen} />
          <Row k={t('ops:drawer.last_telemetry')} v={sinceOrNever(device.lastTelemetryAt)} />
          <Row k={t('ops:drawer.last_health')} v={sinceOrNever(device.lastHealthAt)} />
        </dl>

        <button onClick={() => setAdvancedOpen(true)}
          className="w-full inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
          <ServerCog size={14} /> {t('ops:drawer.advanced_details')}
        </button>

        {/* A seção INTEIRA, e não só o conteúdo: esconder os comandos e deixar o
            título deixava um cabeçalho "COMANDOS" com nada embaixo. Comandos ao
            equipamento exigem engineer no backend. */}
        {can('engineer') && (
          <div className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('ops:drawer.commands')}</span>
            <DeviceCommands deviceName={device.name} canUpdate={!!device.capabilities?.update} />
          </div>
        )}

        {/* Forçar logoff é ação de developer (routes.go). */}
        {can('developer') && (
        <div className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('ops:drawer.debug')}</span>
          <button onClick={forceLogoff} disabled={!loggedIn || loggingOff}
            className="w-full inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-md border border-amber-200 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed">
            <LogOut size={14} /> {loggingOff ? t('ops:drawer.forcing_logoff') : t('ops:drawer.force_logoff')}
          </button>
          {logoffResult && (
            <p className={`text-[11px] leading-snug ${logoffResult.ok ? 'text-status-op' : 'text-status-down'}`}>{logoffResult.msg}</p>
          )}
          <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-snug">{t('ops:drawer.force_logoff_hint')}</p>
        </div>
        )}
      </div>

      {actionsOpen && <DeviceActionsModal key={device.name} device={device} onClose={() => setActionsOpen(false)} />}
      {advancedOpen && (
        <AdvancedDeviceModal
          vmcName={device.name}
          displayName={eq.name}
          fetchUrl={`/api/devices/${device.name}/device-info`}
          onClose={() => setAdvancedOpen(false)}
        />
      )}
      {confirmDialog}
    </Drawer>
  )
}
